import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Play } from 'lucide-react';
import { comprehensiveCreativeAnalysis } from '../../services/comprehensiveCreativeAnalysis';
import { useAuth } from '../../contexts/AuthContext';
import TimeSeriesChart from '../TimeSeriesChart';
import ComparisonChart from '../ComparisonChart';
import ReportSummary from '../ReportSummary';

const TopPerformingAds = ({ filters, onFilterChange }) => {
  const { getAccountId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [topPerformersData, setTopPerformersData] = useState(null); // Start with null to show loading instead of error on initial render
  // Use correlation filter from global Dashboard Configuration instead of local state
  const correlationMetric = filters?.correlationMetric || 'roas';
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  const [viewMode, setViewMode] = useState('card'); // 'card', 'time-trend', 'comparison'
  const requestIdRef = useRef(0);

  // Render thumbnail component (copied from comprehensive report)
  const renderThumbnail = (assetUrl, mediaFormat, adName, className = "w-16 h-16") => {
    if (!assetUrl) {
      return (
        <div className={`${className} bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs`}>
          No Asset
        </div>
      );
    }

    const openModal = () => {
      setPreviewModal({
        isOpen: true,
        assetUrl,
        adName,
        mediaFormat
      });
    };

    if (mediaFormat === 'Video') {
      return (
        <div className={`${className} relative bg-gray-200 rounded overflow-hidden cursor-pointer`} onClick={openModal}>
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            preload="metadata"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-2xl">
            ▶️
          </div>
          <div 
            className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-xs hidden"
          >
            Click to View
          </div>
        </div>
      );
    }

    return (
      <img
        src={assetUrl}
        alt={adName}
        className={`${className} object-cover rounded cursor-pointer hover:opacity-80 transition-opacity`}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
        onClick={openModal}
      />
    );
  };

  // Generate date range condition from filters
  const getDateRange = () => {
    // Check for custom date range first
    if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
      console.log('🔄 TopPerformingAds using CUSTOM date range:', filters.startDate, 'to', filters.endDate);
      return `adp.date >= '${filters.startDate}' AND adp.date <= '${filters.endDate}'`;
    }
    // Fall back to preset days - use timezone-aware CURRENT_DATE to match time series chart
    if (filters?.dateRange && typeof filters.dateRange === 'number') {
      console.log('🔄 TopPerformingAds using PRESET date range:', filters.dateRange, 'days');
      return `adp.date >= (CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${filters.dateRange} days'`;
    }
    return `adp.date >= (CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '7 days'`;
  };

  // Generate ad name filter from filters
  const getAdNameFilter = () => {
    if (filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0) {
      // Return the proper object format for the service
      return {
        keywords: filters.adNameFilterKeywords.filter(k => k.trim()),
        logic: filters.adNameFilterLogic || 'AND',
        mode: filters.adNameFilterMode || 'include'
      };
    }
    return null;
  };

  // Load top performers data - always fetch fresh (no cache) to match time series graph
  const loadTopPerformers = async () => {
    const accountId = getAccountId();
    if (!accountId) {
      return;
    }

    // Prevent multiple simultaneous requests
    if (loading) {
      return;
    }

    // Generate unique request ID to handle race conditions
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    try {
      const dateRange = getDateRange();
      const adNameFilter = getAdNameFilter();
      
      // Always fetch fresh data (skip cache) to ensure 100% consistency with time series graph
      const data = await comprehensiveCreativeAnalysis.getTopPerformersAnalysisFormatted(
        accountId, 
        dateRange, 
        adNameFilter
      );

      // Only update if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setTopPerformersData(data);
      }
    } catch (error) {
      console.error('❌ Error loading top performers:', error);
      console.error('❌ Top performers filters being used:', { dateRange: getDateRange(), adNameFilter: getAdNameFilter() });
      // Set empty data structure on error to show appropriate message
      if (currentRequestId === requestIdRef.current) {
        setTopPerformersData({ byROAS: [], bySpend: [] });
      }
    } finally {
      // Only set loading to false if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!filters) {
      console.log('⏳ TopPerformingAds waiting for filters...');
      return;
    }
    console.log('🔄 TopPerformingAds filters changed, reloading...', filters);
    loadTopPerformers();
  }, [filters]);



  // Get the appropriate dataset based on correlation metric from global filter
  let topPerformersDataToUse = [];
  
  if (topPerformersData?.byROAS && topPerformersData?.bySpend) {
    if (correlationMetric === 'roas') {
      topPerformersDataToUse = Array.isArray(topPerformersData.byROAS) ? topPerformersData.byROAS : [];
    } else if (correlationMetric === 'spend') {
      topPerformersDataToUse = Array.isArray(topPerformersData.bySpend) ? topPerformersData.bySpend : [];
    } else {
      // For revenue and ctr, we'll need to sort the ROAS data by the selected metric
      // For now, fall back to ROAS data and we'll sort it later
      topPerformersDataToUse = Array.isArray(topPerformersData.byROAS) ? topPerformersData.byROAS : [];
    }
  } else if (topPerformersData) {
    // Fallback for legacy data structure
    topPerformersDataToUse = Array.isArray(topPerformersData) ? topPerformersData : [];
  }
  
  // Additional safety check
  if (!Array.isArray(topPerformersDataToUse)) {
    topPerformersDataToUse = [];
  }
  
  // Filter out ads without assets and validate data structure, then sort by correlation metric
  let sortedTopPerformers = topPerformersDataToUse
    .filter(ad => {
      if (!ad || typeof ad !== 'object' || !ad.asset_url || ad.asset_url.trim() === '') return false;
      // Filter out CPA = 0 (no purchases) when CPA is the selected metric
      if (correlationMetric === 'cpa' && parseFloat(ad.cpa || 0) === 0) return false;
      return true;
    })
    .slice(); // Create a copy to avoid mutating original array

  // Apply sorting based on correlation metric
  if (correlationMetric === 'revenue') {
    sortedTopPerformers.sort((a, b) => parseFloat(b.total_revenue || 0) - parseFloat(a.total_revenue || 0));
  } else if (correlationMetric === 'ctr') {
    sortedTopPerformers.sort((a, b) => parseFloat(b.ctr || 0) - parseFloat(a.ctr || 0));
  } else if (correlationMetric === 'cpa') {
    // For CPA, lower is better, so reverse sort
    sortedTopPerformers.sort((a, b) => parseFloat(a.cpa || Infinity) - parseFloat(b.cpa || Infinity));
  }
  // For 'roas' and 'spend', the data is already sorted from the service

  sortedTopPerformers = sortedTopPerformers.slice(0, 10); // Take top 10

  // Handle chat button click for specific ad (similar to TimeTrends pattern)
  const handleAdChat = (ad, index) => {
    const metricDisplay = correlationMetric.toUpperCase();
    const dateRangeText = filters?.dateRange ? `last ${filters.dateRange} days` : 'last 7 days';
    const keywordsText = filters?.adNameFilterKeywords?.length > 0 
      ? ` with keywords: ${filters.adNameFilterKeywords.join(', ')}`
      : '';
    
    // Get the metric value for display
    let metricValue = '';
    if (correlationMetric === 'revenue') {
      metricValue = `$${parseFloat(ad.total_revenue || 0).toFixed(2)} revenue`;
    } else if (correlationMetric === 'ctr') {
      metricValue = `${parseFloat(ad.ctr || 0).toFixed(2)}% CTR`;
    } else if (correlationMetric === 'spend') {
      metricValue = `$${parseFloat(ad.total_spend || 0).toFixed(2)} spend`;
    } else if (correlationMetric === 'cpa') {
      metricValue = `$${parseFloat(ad.cpa || 0).toFixed(2)} CPA`;
    } else {
      metricValue = `${parseFloat(ad.roas || 0).toFixed(2)}x ROAS`;
    }
    
    const prompt = `Analyze this top performing ad (#${index + 1} by ${metricDisplay}): "${ad.ad_name}" - it has ${ad.media_format} format, ${metricValue}, $${parseFloat(ad.total_spend || 0).toFixed(2)} spend, ${parseFloat(ad.roas || 0).toFixed(2)}x ROAS, ${parseFloat(ad.ctr || 0).toFixed(2)}% CTR over the ${dateRangeText}${keywordsText}. What makes this ad successful and how can I replicate its performance?`;
    
    // Auto-populate the chat input and open chat (handled by parent Dashboard component)
    if (onFilterChange && typeof onFilterChange === 'function') {
      onFilterChange({ type: 'chat', message: prompt });
    }
  };

  // Preview Modal Component (copied from comprehensive report)
  const renderPreviewModal = () => {
    if (!previewModal.isOpen) return null;

    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 truncate pr-4" title={previewModal.adName}>
              {previewModal.adName}
            </h3>
            <button
              onClick={() => setPreviewModal({ ...previewModal, isOpen: false })}
              className="text-gray-400 hover:text-gray-600 text-2xl flex-shrink-0"
              title="Close"
            >
              ×
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            {previewModal.mediaFormat === 'Video' ? (
              <video 
                className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                controls
                autoPlay
                preload="metadata"
                onError={(e) => {
                  console.error('Video preview error:', e);
                }}
              >
                <source src={previewModal.assetUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={previewModal.assetUrl} 
                alt={previewModal.adName}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  console.error('Image preview error:', e);
                }}
              />
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(previewModal.assetUrl).then(() => {
                      // Show a temporary success message (optional)
                      const btn = document.activeElement;
                      const originalText = btn.textContent;
                      btn.textContent = '✓ Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText;
                      }, 2000);
                    }).catch(err => {
                      console.error('Failed to copy URL:', err);
                      alert('Failed to copy URL. Please try again.');
                    });
                  }}
                  className="px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                  title="Copy asset URL to clipboard"
                >
                  Copy URL
                </button>
                <button
                  onClick={() => setPreviewModal({ ...previewModal, isOpen: false })}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Top Performing Ads</h3>
            <p className="text-gray-600 mb-4">
              Analyzing ad performance to identify your highest {correlationMetric.toUpperCase()} ads...
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 max-w-md mx-auto">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-5 h-5 text-purple-400">⏱️</div>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-purple-800">
                    This analysis applies spend thresholds and may take 30-60 seconds to complete.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">
                🏆 Top Performing Ads
              </h3>
              <p className="text-sm text-gray-500">Identify your highest performing creative assets</p>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-1.5 shadow-inner border border-gray-200">
              <button
                onClick={() => setViewMode('card')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'card'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Card
              </button>
              <button
                onClick={() => setViewMode('time-trend')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'time-trend'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Time Trend
              </button>
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'comparison'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Comparison
              </button>
            </div>
          </div>
          
          {/* Filter Status Indicators */}
          <div className="flex items-center flex-wrap gap-3 mt-3">
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 rounded-lg border border-indigo-200">
              <span className="text-xs font-semibold text-indigo-700">
                Ranking by: <span className="text-indigo-900">{correlationMetric.toUpperCase()}</span>
              </span>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200">
              <span className="text-xs font-semibold text-purple-700">
                Period: <span className="text-purple-900">Last {filters?.dateRange || 7} days</span>
              </span>
            </div>
            {filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0 && (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-pink-50 rounded-lg border border-pink-200">
                <span className="text-xs font-semibold text-pink-700">
                  Keywords: <span className="text-pink-900">{filters.adNameFilterKeywords.join(', ')}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* AI Summary Section - Right after title */}
        {!loading && topPerformersData && sortedTopPerformers.length > 0 && (
          <ReportSummary 
            reportType="top-performing"
            reportData={sortedTopPerformers}
            filters={filters}
            accountId={getAccountId()}
          />
        )}

        {loading || !topPerformersData ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading top performers...</p>
          </div>
        ) : topPerformersData && ((topPerformersData.byROAS && topPerformersData.byROAS.length > 0) || (topPerformersData.bySpend && topPerformersData.bySpend.length > 0)) ? (
          <div>
            {/* Card View */}
            {viewMode === 'card' && sortedTopPerformers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {sortedTopPerformers.map((ad, index) => {
                  // Validate ad object
                  if (!ad || typeof ad !== 'object') {
                    console.error('❌ Invalid ad object at index', index, ':', ad);
                    return null;
                  }
                  
                  const isTop3 = index < 3;
                  
                  return (
                    <div 
                      key={index} 
                      className={`relative rounded-2xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group cursor-pointer ${
                        isTop3
                          ? 'bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border-2 border-emerald-300 shadow-lg ring-2 ring-emerald-200 ring-opacity-50'
                          : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
                      }`}
                      onClick={() => handleAdChat(ad, index)}
                      title="Click to analyze this ad with AI"
                    >
                      {/* Rank Badge for Top 3 */}
                      {isTop3 && (
                        <div className="absolute top-3 left-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-base font-bold shadow-xl ring-2 ring-white">
                          {index + 1}
                        </div>
                      )}
                      
                      {/* Media Preview */}
                      <div 
                        className={`relative aspect-square overflow-hidden ${
                          isTop3 ? 'bg-gradient-to-br from-emerald-100 to-emerald-50' : 'bg-gray-100'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewModal({
                            isOpen: true,
                            assetUrl: ad.asset_url,
                            mediaFormat: ad.media_format,
                            adName: ad.ad_name
                          });
                        }}
                        title="Click to preview media"
                      >
                        {isTop3 && (
                          <div className="absolute inset-0 z-0 opacity-20 bg-gradient-to-br from-emerald-400 to-emerald-300" />
                        )}
                        {ad.media_format === 'Video' ? (
                          <div className="relative w-full h-full">
                            <video
                              src={ad.asset_url}
                              className="w-full h-full object-cover"
                              preload="metadata"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                              <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                                <Play className="w-8 h-8 text-gray-900" />
                              </div>
                            </div>
                            <div className="absolute top-2 right-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg">
                              Video
                            </div>
                            <div 
                              className="absolute inset-0 bg-gray-200 flex-col items-center justify-center text-gray-400 text-sm hidden"
                            >
                              <div className="text-4xl mb-2">🎥</div>
                              <div>Video Unavailable</div>
                            </div>
                          </div>
                        ) : (
                          <div className="relative w-full h-full">
                            <img
                              src={ad.asset_url}
                              alt={ad.ad_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="absolute top-2 right-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg">
                              Image
                            </div>
                            <div 
                              className="absolute inset-0 bg-gray-200 flex-col items-center justify-center text-gray-400 text-sm hidden"
                            >
                              <div className="text-4xl mb-2">🖼️</div>
                              <div>Image Unavailable</div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Card Content */}
                      <div className={`p-5 ${isTop3 ? 'bg-white/80 backdrop-blur-sm' : ''}`}>
                        {/* Ad Name */}
                        <h4 className={`font-bold text-base mb-3 line-clamp-2 min-h-[2.5rem] ${
                          isTop3 ? 'text-emerald-900' : 'text-gray-900'
                        }`} title={ad.ad_name}>
                          {ad.ad_name || 'Unknown Ad'}
                        </h4>
                        
                        {/* Metrics Tags */}
                        <div className="space-y-3">
                          {/* Primary Metrics Row */}
                          <div className="flex flex-wrap gap-2">
                            <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                              correlationMetric === 'roas' 
                                ? isTop3
                                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                                  : 'bg-green-100 text-green-800 border-2 border-green-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              <span className="mr-1.5">ROAS:</span> 
                              <span className="text-sm">{parseFloat(ad.roas || 0).toFixed(2)}x</span>
                            </div>
                            <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                              correlationMetric === 'spend' 
                                ? isTop3
                                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                                  : 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              <span className="mr-1.5">Spend:</span> 
                              <span className="text-sm">${parseFloat(ad.total_spend || 0).toFixed(0)}</span>
                            </div>
                          </div>
                          
                          {/* Secondary Metrics Row */}
                          <div className="flex flex-wrap gap-2">
                            <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                              correlationMetric === 'ctr' 
                                ? isTop3
                                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                                  : 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              <span className="mr-1.5">CTR:</span> 
                              <span className="text-sm">{parseFloat(ad.ctr || 0).toFixed(2)}%</span>
                            </div>
                            <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                              correlationMetric === 'cpa' 
                                ? isTop3
                                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                                  : 'bg-orange-100 text-orange-800 border-2 border-orange-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              <span className="mr-1.5">CPA:</span> 
                              <span className="text-sm">${parseFloat(ad.cpa || 0).toFixed(2)}</span>
                            </div>
                          </div>
                          
                          {/* Video Metrics (if video) */}
                          {ad.media_format === 'Video' && (ad.hook_rate || ad.hookrate || ad.three_second_video_view_rate || ad.hold_rate || ad.holdrate) && (
                            <div className="flex flex-wrap gap-2">
                              {(ad.hook_rate || ad.hookrate || ad.three_second_video_view_rate) && (
                                <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                                  isTop3 ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800 border-2 border-indigo-300'
                                }`} title="Hook Rate: Retention % of first 3 seconds">
                                  <span className="mr-1.5">Hook:</span> 
                                  <span className="text-sm">{parseFloat(ad.hook_rate || ad.hookrate || ad.three_second_video_view_rate || 0).toFixed(1)}%</span>
                                </div>
                              )}
                              {(ad.hold_rate || ad.holdrate) && (
                                <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                                  isTop3 ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-pink-100 text-pink-800 border-2 border-pink-300'
                                }`} title="Hold Rate: Average watch length / video length">
                                  <span className="mr-1.5">Hold:</span> 
                                  <span className="text-sm">{parseFloat(ad.hold_rate || ad.holdrate || 0).toFixed(1)}%</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Revenue & Conversions */}
                          <div className={`flex flex-wrap gap-2 text-xs font-semibold ${
                            isTop3 ? 'text-emerald-700' : 'text-gray-600'
                          }`}>
                            <div className="px-2 py-1 bg-gray-50 rounded-lg">
                              <span className="mr-1">Revenue:</span> 
                              <span className="text-sm">${parseFloat(ad.total_revenue || 0).toFixed(0)}</span>
                            </div>
                            <div className="px-2 py-1 bg-gray-50 rounded-lg">
                              <span className="mr-1">Conv:</span> 
                              <span className="text-sm">{parseInt(ad.conversions || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* AI Chat Button - Bottom Right with Enhanced Styling */}
                      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAdChat(ad, index);
                          }}
                          className={`p-3 rounded-full shadow-xl pointer-events-auto transition-all duration-300 ${
                            isTop3
                              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                              : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                          }`}
                          title="Analyze with AI"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            )}
            
            {viewMode === 'card' && sortedTopPerformers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🖼️</div>
                <p className="text-gray-500 text-lg mb-2">No ads with assets found</p>
                <p className="text-gray-400 text-sm">
                  All top performing ads in this date range don't have associated images or videos.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <p className="text-gray-500 text-lg mb-2">No top performers data available</p>
            <p className="text-gray-400 text-sm">
              {topPerformersData ? 
                'Top performers data structure is empty or invalid. Please regenerate the report.' : 
                'Generate a report first to see top performing ads.'
              }
            </p>
            {topPerformersData && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                <strong>Debug Info:</strong> Top performers data: {JSON.stringify(topPerformersData, null, 2)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comparison Chart View */}
      {viewMode === 'comparison' && !loading && sortedTopPerformers && sortedTopPerformers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <ComparisonChart
            title={`Top Ads Comparison (by ${correlationMetric.toUpperCase()})`}
            data={sortedTopPerformers.map(ad => ({
              name: (ad.ad_name || 'Unknown').substring(0, 30), // Truncate long names
              spend: parseFloat(ad.total_spend || 0),
              roas: parseFloat(ad.roas || 0)
            }))}
            xAxisKey="name"
            metrics={[
              { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
              { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
            ]}
            height={400}
            maxItems={10}
          />
        </div>
      )}
      
      {/* Time Series Performance Chart View */}
      {viewMode === 'time-trend' && !loading && sortedTopPerformers && sortedTopPerformers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <TimeSeriesChart 
            accountId={getAccountId()}
            ads={sortedTopPerformers}
            dateRangeDays={filters?.dateRange || 7}
            startDate={filters?.dateRangeType === 'custom' ? filters?.startDate : null}
            endDate={filters?.dateRangeType === 'custom' ? filters?.endDate : null}
            numberOfPeriods={14}
          />
        </div>
      )}
      
      {/* Preview Modal */}
      {renderPreviewModal()}
    </div>
  );
};

export default TopPerformingAds;